// =============================================================================
// Scroll choreography of the business card ("Still here?" section).
//
// The card falls from above the screen while spinning, shows its recto (first
// pose of the design), flips over to its verso while lying down (isometric
// pose) and lands flat below the call to action (last pose). Everything is a
// pure function of the section progress (0 → 1), shared by the WebGL scene and
// its CSS fallback. No three.js here so the fallback stays light.
// =============================================================================

/** Camera of the scene: narrow lens, looking down at the floor */
export const CAMERA = {
  fov: 26,
  /** Angle between the view direction and the floor, in degrees */
  elevation: 34,
  distance: 9,
} as const;

/** Design ratio of the card (1005 × 650) */
export const CARD_RATIO = 650 / 1005;

/** Key moments of the sequence, also used by the HTML layers */
export const TIMELINE = {
  /** The title fades out while the card falls in front of it */
  title: [0.06, 0.2],
  /** The card touches the floor */
  landing: 0.78,
  /** Call to action, word by word, then its button */
  cta: [0.74, 0.88],
  button: [0.88, 0.95],
} as const;

// =============================================================================
// KEYFRAMES
// =============================================================================

type Keyframes = ReadonlyArray<readonly [progress: number, value: number]>;

type Track = { frames: Keyframes; slopes: number[] };

/**
 * Tangents of a monotone cubic interpolation (Fritsch–Carlson): the curve
 * goes through every keyframe, without overshooting between them.
 */
function createTrack(frames: Keyframes): Track {
  const count = frames.length;
  const slopes = frames.map((_, index) => {
    if (index === 0 || index === count - 1) return 0;
    const [x0, y0] = frames[index - 1];
    const [x1, y1] = frames[index];
    const [x2, y2] = frames[index + 1];
    const h0 = x1 - x0;
    const h1 = x2 - x1;
    const d0 = (y1 - y0) / h0;
    const d1 = (y2 - y1) / h1;
    if (d0 * d1 <= 0) return 0;
    const w0 = 2 * h1 + h0;
    const w1 = h1 + 2 * h0;
    return (w0 + w1) / (w0 / d0 + w1 / d1);
  });
  return { frames, slopes };
}

function sample({ frames, slopes }: Track, t: number): number {
  const last = frames.length - 1;
  if (t <= frames[0][0]) return frames[0][1];
  if (t >= frames[last][0]) return frames[last][1];
  let index = 0;
  while (t > frames[index + 1][0]) index += 1;
  const [x0, y0] = frames[index];
  const [x1, y1] = frames[index + 1];
  const h = x1 - x0;
  const s = (t - x0) / h;
  const s2 = s * s;
  const s3 = s2 * s;
  return (
    (2 * s3 - 3 * s2 + 1) * y0 +
    (s3 - 2 * s2 + s) * h * slopes[index] +
    (-2 * s3 + 3 * s2) * y1 +
    (s3 - s2) * h * slopes[index + 1]
  );
}

const DEG = Math.PI / 180;

/**
 * Orientation of the card, composed as yaw (around the vertical axis of the
 * world) · tilt (around the horizontal axis of the screen) · roll (around the
 * normal of the card) · flip (around the vertical axis of the card, like a
 * page). The recto faces the camera at tilt = -elevation, the card lies flat
 * at tilt = -90° and flip = 180° shows the verso upright.
 */
const ROTATIONS = {
  yaw: createTrack([
    [0, -26],
    [0.2, -10],
    [0.32, 0],
    [0.55, 44],
    [0.64, 38],
    [0.78, -4],
    [1, -5],
  ]),
  tilt: createTrack([
    [0, -8],
    [0.2, -26],
    [0.32, -30],
    [0.55, -76],
    [0.64, -82],
    [0.78, -90],
    [1, -90],
  ]),
  roll: createTrack([
    [0, -84],
    [0.2, -30],
    [0.32, -24],
    [0.55, -4],
    [0.7, 0],
    [1, 0],
  ]),
  flip: createTrack([
    [0, -220],
    [0.2, -8],
    [0.32, 0],
    [0.55, 180],
    [1, 180],
  ]),
};

/** Horizontal sway, in NDC (-1 left → 1 right) */
const SWAY = createTrack([
  [0, -0.22],
  [0.2, -0.03],
  [0.32, 0.02],
  [0.44, 0.08],
  [0.55, 0.03],
  [0.66, 0],
  [1, 0],
]);

// =============================================================================
// LAYOUT
// =============================================================================

export type StageLayout = {
  /** Width of the landed card, in CSS pixels */
  cardWidth: number;
  /** Heights of the card center on screen, in NDC (-1 bottom → 1 top) */
  startY: number;
  heroY: number;
  isoY: number;
  landY: number;
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

/** Responsive framing: bigger card on phones, lower landing on desktop */
export function getStageLayout(width: number, height: number): StageLayout {
  const portrait = width / height < 0.9;
  const cardWidth = portrait
    ? clamp(width * 0.72, 240, 480)
    : clamp(Math.min(width * 0.4, height * 0.68), 340, 660);
  // Half diagonal of the card on screen, to start fully above the viewport
  const extent = (cardWidth * 0.6) / (height / 2);
  return {
    cardWidth,
    startY: 1 + extent + 0.12,
    heroY: portrait ? 0.1 : 0.06,
    isoY: portrait ? -0.12 : -0.14,
    landY: portrait ? -0.32 : -0.44,
  };
}

// =============================================================================
// POSE
// =============================================================================

export type CardPose = {
  /** Screen position of the card center, in NDC */
  x: number;
  y: number;
  /** Radians, see ROTATIONS */
  yaw: number;
  tilt: number;
  roll: number;
  flip: number;
};

export function createPose(): CardPose {
  return { x: 0, y: 0, yaw: 0, tilt: 0, roll: 0, flip: 0 };
}

/** Vertical track of a layout (memoize it: it depends on the viewport) */
export function createFallTrack(layout: StageLayout): Track {
  const { startY, heroY, isoY, landY } = layout;
  return createTrack([
    [0, startY],
    [0.2, heroY + 0.05],
    [0.32, heroY - 0.02],
    [0.55, isoY],
    [0.64, isoY - (isoY - landY) * 0.3],
    [TIMELINE.landing, landY],
    [1, landY],
  ]);
}

export type FallTrack = ReturnType<typeof createFallTrack>;

/** Pose of the card at a given progress of the section (writes into `out`) */
export function sampleCardPose(
  progress: number,
  fall: FallTrack,
  out: CardPose
): CardPose {
  const t = clamp(progress, 0, 1);
  out.x = sample(SWAY, t);
  out.y = sample(fall, t);
  out.yaw = sample(ROTATIONS.yaw, t) * DEG;
  out.tilt = sample(ROTATIONS.tilt, t) * DEG;
  out.roll = sample(ROTATIONS.roll, t) * DEG;
  out.flip = sample(ROTATIONS.flip, t) * DEG;
  return out;
}

// =============================================================================
